/*
 * Copyright (C) 2018 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { Action } from '@ngrx/store';
import {
  DateGrouping,
  EventDetail,
  EventType,
  FileGroup
} from 'app/page-modules/tenant-management/modules/activity/models';
import { DateTime } from 'luxon';
import { CalcRunType, Range } from 'app/shared/models';

export const CLEAR_STATE = '[Activity] Clear State';

export const LOAD_ACTIVITY = '[Activity] Load Activity';
export const LOAD_ACTIVITY_SUCCESS = '[Activity] Load Activity Success';
export const LOAD_ACTIVITY_ERROR = '[Activity] Load Activity Error';

export const LOAD_EVENT_DETAILS = '[Activity] Load Event Details';
export const LOAD_EVENT_DETAILS_SUCCESS = '[Activity] Load Event Details Success';
export const LOAD_EVENT_DETAILS_ERROR = '[Activity] Load Event Details Error';

export const DOWNLOAD_GROUP_EVENT = '[Activity] Download Group Event';
export const DOWNLOAD_GROUP_EVENT_SUCCESS = '[Activity] Download Group Event Success';
export const DOWNLOAD_GROUP_EVENT_ERROR = '[Activity] Download Group Event Error';

export const SELECT_APPLICABLE_DATE_RANGE = '[Activity] Select Applicable Date Range';
export const SELECT_EVENT_DATE_RANGE = '[Activity] Select Event Date Range';
export const SELECT_CALC_RUN_TYPES = '[Activity] Select Calc Run Types';
export const SELECT_EVENT_TYPES = '[Activity] Select Event Types';

export const CLOSE_MODAL = '[Activity] Close Modal';

// -------------------------------------------------------------------------
// Clear state
export class ClearState implements Action {
  readonly type = CLEAR_STATE;
}

// -------------------------------------------------------------------
/* Loads Activity data. */
export class LoadActivity implements Action {
  readonly type = LOAD_ACTIVITY;

  constructor(
    public applicableDateRange?: Range<DateTime | undefined>,
    public eventDateRange?: Range<DateTime | undefined>
  ) {}
}

/* Successfully loaded Activity data. */
export class LoadActivitySuccess implements Action {
  readonly type = LOAD_ACTIVITY_SUCCESS;

  constructor(public dateGroupings: DateGrouping[]) {}
}

/* Error occurred when loading Activity data. */
export class LoadActivityError implements Action {
  readonly type = LOAD_ACTIVITY_ERROR;

  constructor(public errorMessage: string) {}
}

// -------------------------------------------------------------------
/* Loads Event Details data for the given event ID. */
export class LoadEventDetails implements Action {
  readonly type = LOAD_EVENT_DETAILS;

  constructor(public eventId: string) {}
}

/* Successfully loaded Event Details data. */
export class LoadEventDetailsSuccess implements Action {
  readonly type = LOAD_EVENT_DETAILS_SUCCESS;

  constructor(public eventDetail: EventDetail) {}
}

/* Error occurred when loading Event Details data. */
export class LoadEventDetailsError implements Action {
  readonly type = LOAD_EVENT_DETAILS_ERROR;

  constructor(public errorMessage: string) {}
}

// -------------------------------------------------------------------------
/** Toggle the modal. */
export class CloseModal implements Action {
  readonly type = CLOSE_MODAL;
}

// -------------------------------------------------------------------------
/* Initiates download of File Group data. */
export class DownloadGroupEvent implements Action {
  readonly type = DOWNLOAD_GROUP_EVENT;

  constructor(public downloadId: string, public reason: string) {}
}

/* Successfully loaded File Group data. */
export class DownloadGroupEventSuccess implements Action {
  readonly type = DOWNLOAD_GROUP_EVENT_SUCCESS;

  constructor(public fileGroup: FileGroup, public downloadId: string) {}
}

/* Error occurred when loading File Group data. */
export class DownloadGroupEventError implements Action {
  readonly type = DOWNLOAD_GROUP_EVENT_ERROR;

  constructor(public errorMessage: string, public downloadId: string) {}
}

// -------------------------------------------------------------------
export class SelectApplicableDateRange implements Action {
  readonly type = SELECT_APPLICABLE_DATE_RANGE;

  constructor(public applicableDateRange: Range<DateTime | undefined>) {}
}

// -------------------------------------------------------------------
export class SelectEventDateRange implements Action {
  readonly type = SELECT_EVENT_DATE_RANGE;

  constructor(public eventDateRange: Range<DateTime | undefined>) {}
}

// -------------------------------------------------------------------
export class SelectCalcRunTypes implements Action {
  readonly type = SELECT_CALC_RUN_TYPES;

  constructor(public calcRunTypes: CalcRunType[]) {}
}

// -------------------------------------------------------------------
export class SelectEventTypes implements Action {
  readonly type = SELECT_EVENT_TYPES;

  constructor(public eventTypes: EventType[]) {}
}

// -------------------------------------------------------------------------
export type All =
  | ClearState
  | LoadActivity
  | LoadActivitySuccess
  | LoadActivityError
  | LoadEventDetails
  | LoadEventDetailsSuccess
  | LoadEventDetailsError
  | CloseModal
  | DownloadGroupEvent
  | DownloadGroupEventSuccess
  | DownloadGroupEventError
  | SelectApplicableDateRange
  | SelectEventDateRange
  | SelectCalcRunTypes
  | SelectEventTypes;
